<template>
  <div class="">
    <div v-if="flashMessageService.message.value" class="alert-box alert-info" @click="handleMessageClick('message')">
      <img :src="getIconPath('info')" alt="" class="icon icon--w32 flex-shrink-0">
      <div>
        <h5 class="h5 alert__title">{{ flashMessageService.message.value }}</h5>
      </div>
    </div>

    <div v-if="flashMessageService.success.value" class="alert-box alert-success" @click="handleMessageClick('success')">
      <img :src="getIconPath('success')" alt="" class="icon icon--w32 flex-shrink-0">
      <div>
        <h5 class="h5 alert__title">{{ flashMessageService.success.value }}</h5>
      </div>
    </div>

    <div v-if="flashMessageService.warning.value" class="alert-box alert-warning" @click="handleMessageClick('warning')">
      <img :src="getIconPath('warning')" alt="" class="icon icon--w32 flex-shrink-0">
      <div>
        <h5 class="h5 alert__title">{{ flashMessageService.warning.value }}</h5>
      </div>
    </div>

    <div v-if="flashMessageService.danger.value" class="alert-box alert-danger" @click="handleMessageClick('danger')">
      <img :src="getIconPath('danger')" alt="" class="icon icon--w32 flex-shrink-0">
      <div>
        <h5 class="h5 alert__title">{{ flashMessageService.danger.value }}</h5>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watchEffect } from 'vue';
import flashMessageService from '@/services/flashMessageService';

// Track when messages were displayed to prevent immediate dismissal
const messageTimestamps = {
  message: ref(0),
  success: ref(0),
  warning: ref(0),
  danger: ref(0)
};

// Watch for changes in the message values and update timestamps
function updateTimestamps() {
  if (flashMessageService.message.value) messageTimestamps.message.value = Date.now();
  if (flashMessageService.success.value) messageTimestamps.success.value = Date.now();
  if (flashMessageService.warning.value) messageTimestamps.warning.value = Date.now();
  if (flashMessageService.danger.value) messageTimestamps.danger.value = Date.now();
}

// Call updateTimestamps initially to set timestamps for any messages that are already displayed
updateTimestamps();

// Handle message click - only dismiss if it's been displayed for at least 5 seconds
function handleMessageClick(type) {
  const minDisplayTime = 5000; // 5 seconds minimum display time
  const currentTime = Date.now();
  const messageTime = messageTimestamps[type].value;

  // Only allow dismissal if the message has been displayed for at least 5 seconds
  if (currentTime - messageTime >= minDisplayTime) {
    flashMessageService.removeMessage(type);
  }
}

// Function to get the appropriate icon path based on the message type
function getIconPath(type) {
  return `/images/icons/ico-alert-${type}.svg`;
}

// Watch for changes in the message values
onMounted(() => {
  // Set up watchers for each message type
  const unwatch = [
    watchEffect(() => {
      // Force re-evaluation by accessing the value directly
      const messageValue = flashMessageService.message.value;
      if (messageValue) {
        messageTimestamps.message.value = Date.now();
        console.log('Message timestamp updated:', messageValue);
      }
    }),
    watchEffect(() => {
      const successValue = flashMessageService.success.value;
      if (successValue) {
        messageTimestamps.success.value = Date.now();
        console.log('Success timestamp updated:', successValue);
      }
    }),
    watchEffect(() => {
      const warningValue = flashMessageService.warning.value;
      if (warningValue) {
        messageTimestamps.warning.value = Date.now();
        console.log('Warning timestamp updated:', warningValue);
      }
    }),
    watchEffect(() => {
      const dangerValue = flashMessageService.danger.value;
      if (dangerValue) {
        messageTimestamps.danger.value = Date.now();
        console.log('Danger timestamp updated:', dangerValue);
      }
    })
  ];

  // Clean up watchers on unmount
  onUnmounted(() => {
    unwatch.forEach(unwatchFn => unwatchFn());
  });
});

// Make the flash message service available to the template
defineExpose({
  flashMessageService
});
</script>

<style scoped>
.flash-messages {
  margin-bottom: 15px;
}

.alert-box {
  --bs-alert-color: #007B96;
  --bs-alert-bg: #D0F2F7;
  --bs-alert-border-color: #9EE4F0;
  --bs-alert-link-color: #007B96;

  animation-name: fadeOutUp;
  animation-duration: 300ms;
  animation-fill-mode: forwards;
  animation-delay: 5000ms;

  background-color: #ffffff;

  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  max-width: 558px;
  width: calc(100% - 24px);
  min-height: 80px;
  padding: 16px 20px;
  z-index: 2048;

  display: flex;
  align-items: center;

  box-shadow: 0 4px 6px 2px rgba(0, 0, 0, 0.10);
  cursor: pointer;
}

.alert-box:after {
  content: '✖';
  color: var(--bs-alert-color);
  line-height: 1;
  position: absolute;
  top: 14px;
  right: 16px;
  cursor: pointer;
}

.alert-box .alert__title {
  color: #007B96;
}

.alert-box .icon {
  margin-right: 20px;
}

.alert-box:hover:after {
  transform: scale(1.1);
}

@media (max-width: 576px) {
  .alert-box {
    top: 12px;
  }
}

.alert__title {
  margin-bottom: 4px;
}

.alert-box.alert-success {
  --bs-alert-color: #2F814C;
  --bs-alert-bg: #E0F3E6;
  --bs-alert-border-color: #C1E7CD;
  --bs-alert-link-color: #2F814C;

  background-color: #E0F3E6;
}

.alert-box.alert-success .alert__title {
  color: #2F814C;
}

.alert-box.alert-warning {
  --bs-alert-color: #BA792C;
  --bs-alert-bg: #FFF1DD;
  --bs-alert-border-color: #FFE4B9;
  --bs-alert-link-color: #BA792C;

  background-color: #FFF1DD;
}

.alert-box.alert-warning .alert__title {
  color: #BA792C;
}

.alert-box.alert-danger {
  --bs-alert-color: #A21F29;
  --bs-alert-bg: #FBDADD;
  --bs-alert-border-color: #F8B6BA;
  --bs-alert-link-color: #A21F29;

  background-color: #FBDADD;
}

.alert-box.alert-danger .alert__title {
  color: #A21F29;
}

.alert-box.alert-info {
  --bs-alert-color: #007B96;
  --bs-alert-bg: #D0F2F7;
  --bs-alert-border-color: #9EE4F0;
  --bs-alert-link-color: #007B96;

  background-color: #D0F2F7;
}

.alert-box.alert-info .alert__title {
  color: #007B96;
}

@keyframes fadeOutUp {
  0% {
    opacity: 1;
    transform: translate3d(-50%, 0, 0);
  }

  100% {
    opacity: 0;
    transform: translate3d(-50%, -100%, 0);
    pointer-events: none;
  }
}
</style>
